import React from 'react';
import { observer } from 'mobx-react';
import styles from './Pagination.module.css';
import { Button } from '../../../components/UI/Button/Button';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = observer(
  ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    loading = false,
  }) => {
    if (totalPages <= 1) {
      return null;
    }

    const getVisiblePages = () => {
      const visiblePages: (number | string)[] = [];
      const maxVisible = 7;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          visiblePages.push(i);
        }
      } else {
        visiblePages.push(1);

        if (currentPage > 4) {
          visiblePages.push('...');
        }

        const start = Math.max(2, currentPage - 2);
        const end = Math.min(totalPages - 1, currentPage + 2);

        for (let i = start; i <= end; i++) {
          visiblePages.push(i);
        }

        if (currentPage < totalPages - 3) {
          visiblePages.push('...');
        }

        visiblePages.push(totalPages);
      }

      return visiblePages;
    };

    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
        onPageChange(page);
      }
    };

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className={styles.container}>
        <div className={styles.info}>
          Показано {startItem}-{endItem} из {totalItems} записей
        </div>

        <div className={styles.pagination}>
          <Button
            appearance="link"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className={styles.navButton}
          >
            ← Предыдущая
          </Button>

          <div className={styles.pages}>
            {getVisiblePages().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className={styles.ellipsis}>...</span>
                ) : (
                  <button
                    className={`${styles.pageButton} ${
                      page === currentPage ? styles.active : ''
                    }`}
                    onClick={() => handlePageChange(page as number)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <Button
            appearance="link"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            className={styles.navButton}
          >
            Следующая →
          </Button>
        </div>
      </div>
    );
  },
);
